import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { runsAPI, templatesAPI } from '../utils/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Loader2, Rocket, Code2, RefreshCw, Plus, LayoutTemplate } from 'lucide-react';
import { toast } from 'sonner';

const STACKS = [
  { id: 'react', label: 'React', desc: 'Application React avec Vite ou CRA' },
  { id: 'vue', label: 'Vue.js', desc: 'Application Vue 3 avec Vite' },
  { id: 'node', label: 'Node.js', desc: 'API Node.js / Express' },
  { id: 'python', label: 'Python', desc: 'Application Python / FastAPI / Flask' },
  { id: 'laravel', label: 'Laravel', desc: 'Application Laravel PHP' },
];

const MODES = [
  { id: 'new', label: 'Nouveau projet', icon: Plus, desc: 'Générer un projet from scratch' },
  { id: 'improve', label: 'Améliorer un projet', icon: RefreshCw, desc: 'Modifier / ajouter à un projet existant' },
];

export default function NewProjectPage() {
  const [mode, setMode] = useState('new');
  const [goal, setGoal] = useState('');
  const [stack, setStack] = useState('react');
  const [maxSteps, setMaxSteps] = useState(20);
  const [budget, setBudget] = useState(5.0);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sourceRunId, setSourceRunId] = useState('');
  const [completedRuns, setCompletedRuns] = useState([]);
  const [loadingRuns, setLoadingRuns] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState('');
  const navigate = useNavigate();

  // Load completed runs when "improve" mode is selected
  useEffect(() => {
    if (mode === 'improve') {
      setLoadingRuns(true);
      runsAPI.list(50, 0)
        .then((res) => {
          const runs = (res.data || []).filter(
            (r) => r.status === 'completed' && r.project_path
          );
          setCompletedRuns(runs);
        })
        .catch(() => setCompletedRuns([]))
        .finally(() => setLoadingRuns(false));
    }
  }, [mode]);

  // Load templates matching current stack
  useEffect(() => {
    if (mode === 'new') {
      templatesAPI.list(stack)
        .then((res) => setTemplates(res.data || []))
        .catch(() => setTemplates([]));
    }
  }, [mode, stack]);

  // Reset template when switching stack
  useEffect(() => {
    setTemplateId('');
  }, [stack]);

  // Auto-set stack when selecting a source run
  useEffect(() => {
    if (sourceRunId) {
      const run = completedRuns.find((r) => r.id === sourceRunId);
      if (run?.stack) setStack(run.stack);
    }
  }, [sourceRunId, completedRuns]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (goal.trim().length < 10) {
      setError('La description doit contenir au moins 10 caractères');
      return;
    }
    if (mode === 'improve' && !sourceRunId) {
      setError('Sélectionnez un projet existant à améliorer');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const payload = {
        goal: goal.trim(),
        stack,
        max_steps: maxSteps,
        daily_budget_eur: budget,
      };
      if (mode === 'improve' && sourceRunId) {
        payload.source_run_id = sourceRunId;
      }
      if (mode === 'new' && templateId) {
        payload.template_id = templateId;
      }
      const res = await runsAPI.create(payload);
      toast.success(mode === 'improve' ? 'Amélioration lancée !' : 'Projet lancé !');
      navigate(`/projects/${res.data.id}`);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Erreur lors de la création';
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-foreground">Nouveau projet</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Créez un projet from scratch ou améliorez un projet existant.
        </p>
      </div>

      {/* Mode selection */}
      <div className="grid grid-cols-2 gap-3">
        {MODES.map((m) => {
          const Icon = m.icon;
          return (
            <button
              key={m.id}
              type="button"
              onClick={() => { setMode(m.id); setError(''); }}
              className={`flex flex-col items-start p-4 rounded-lg border text-left transition-colors ${
                mode === m.id
                  ? 'border-primary bg-primary/5 ring-1 ring-primary'
                  : 'border-border hover:border-muted-foreground/30 hover:bg-accent/50'
              }`}
            >
              <div className="flex items-center gap-2">
                <Icon className={`w-4 h-4 ${mode === m.id ? 'text-primary' : 'text-muted-foreground'}`} />
                <span className="text-sm font-medium">{m.label}</span>
              </div>
              <span className="text-xs text-muted-foreground mt-1">{m.desc}</span>
            </button>
          );
        })}
      </div>

      <Card className="bg-card border-border">
        <CardContent className="pt-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {/* Source project selection (improve mode only) */}
            {mode === 'improve' && (
              <div className="space-y-2">
                <Label className="text-sm font-medium">Projet source</Label>
                {loadingRuns ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Chargement des projets...
                  </div>
                ) : completedRuns.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-2">
                    Aucun projet terminé disponible. Créez d'abord un projet.
                  </p>
                ) : (
                  <select
                    value={sourceRunId}
                    onChange={(e) => setSourceRunId(e.target.value)}
                    className="flex w-full rounded-lg border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <option value="">-- Sélectionnez un projet --</option>
                    {completedRuns.map((run) => (
                      <option key={run.id} value={run.id}>
                        [{run.stack}] {run.goal?.substring(0, 80)}{run.goal?.length > 80 ? '...' : ''} ({new Date(run.created_at).toLocaleDateString()})
                      </option>
                    ))}
                  </select>
                )}
              </div>
            )}

            {/* Prompt */}
            <div className="space-y-2">
              <Label htmlFor="goal" className="text-sm font-medium">
                {mode === 'improve' ? 'Décrivez les modifications à apporter' : 'Description du projet'}
              </Label>
              <textarea
                id="goal"
                value={goal}
                onChange={(e) => setGoal(e.target.value)}
                placeholder={
                  mode === 'improve'
                    ? 'Ex: Ajouter les vues Blade (index, create, edit, show) pour toutes les ressources existantes avec formulaires et tables...'
                    : 'Ex: Créer une application de gestion de tâches avec authentification, CRUD complet, et interface responsive...'
                }
                rows={5}
                className="flex w-full rounded-lg border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 resize-none"
                required
                minLength={10}
              />
              <p className="text-xs text-muted-foreground">
                {goal.length}/2000 caractères — Soyez aussi précis que possible
              </p>
            </div>

            {/* Stack (hidden in improve mode since it's auto-set from source) */}
            {mode === 'new' && (
              <div className="space-y-2">
                <Label className="text-sm font-medium">Stack technique</Label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {STACKS.map((s) => (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => setStack(s.id)}
                      className={`flex flex-col items-start p-3 rounded-lg border text-left transition-colors ${
                        stack === s.id
                          ? 'border-primary bg-primary/5 ring-1 ring-primary'
                          : 'border-border hover:border-muted-foreground/30 hover:bg-accent/50'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <Code2 className={`w-4 h-4 ${stack === s.id ? 'text-primary' : 'text-muted-foreground'}`} />
                        <span className="text-sm font-medium">{s.label}</span>
                      </div>
                      <span className="text-xs text-muted-foreground mt-1">{s.desc}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Template selection (new project only) */}
            {mode === 'new' && templates.length > 0 && (
              <div className="space-y-2">
                <Label className="text-sm font-medium flex items-center gap-2">
                  <LayoutTemplate className="w-4 h-4 text-muted-foreground" />
                  Template de départ (optionnel)
                </Label>
                <div className="grid grid-cols-1 gap-2">
                  <button
                    type="button"
                    onClick={() => setTemplateId('')}
                    className={`flex items-start p-3 rounded-lg border text-left transition-colors ${
                      !templateId
                        ? 'border-primary bg-primary/5 ring-1 ring-primary'
                        : 'border-border hover:border-muted-foreground/30 hover:bg-accent/50'
                    }`}
                  >
                    <div>
                      <span className="text-sm font-medium">Projet vide</span>
                      <p className="text-xs text-muted-foreground mt-0.5">Scaffold standard sans fichiers pré-configurés</p>
                    </div>
                  </button>
                  {templates.map((tpl) => (
                    <button
                      key={tpl.id}
                      type="button"
                      onClick={() => setTemplateId(tpl.id)}
                      className={`flex items-start p-3 rounded-lg border text-left transition-colors ${
                        templateId === tpl.id
                          ? 'border-primary bg-primary/5 ring-1 ring-primary'
                          : 'border-border hover:border-muted-foreground/30 hover:bg-accent/50'
                      }`}
                    >
                      <div>
                        <span className="text-sm font-medium">{tpl.name}</span>
                        <p className="text-xs text-muted-foreground mt-0.5">{tpl.description}</p>
                        {tpl.tags && tpl.tags.length > 0 && (
                          <div className="flex gap-1 mt-1.5">
                            {tpl.tags.map((tag) => (
                              <span key={tag} className="px-1.5 py-0.5 rounded text-[10px] bg-muted text-muted-foreground">
                                {tag}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Stack badge in improve mode */}
            {mode === 'improve' && sourceRunId && (
              <div className="flex items-center gap-2">
                <Label className="text-sm font-medium">Stack :</Label>
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-primary/10 text-primary text-sm font-medium">
                  <Code2 className="w-3 h-3" />
                  {STACKS.find((s) => s.id === stack)?.label || stack}
                </span>
              </div>
            )}

            {/* Options avancées */}
            <div>
              <button
                type="button"
                onClick={() => setShowAdvanced(!showAdvanced)}
                className="text-xs text-primary hover:underline"
              >
                {showAdvanced ? 'Masquer' : 'Afficher'} les options avancées
              </button>

              {showAdvanced && (
                <div className="grid grid-cols-2 gap-4 mt-3">
                  <div className="space-y-2">
                    <Label className="text-xs">Étapes max</Label>
                    <input
                      type="number"
                      value={maxSteps}
                      onChange={(e) => setMaxSteps(parseInt(e.target.value) || 20)}
                      min={1}
                      max={50}
                      className="flex w-full rounded-lg border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">Budget quotidien (EUR)</Label>
                    <input
                      type="number"
                      value={budget}
                      onChange={(e) => setBudget(parseFloat(e.target.value) || 5.0)}
                      min={0.1}
                      max={100}
                      step={0.1}
                      className="flex w-full rounded-lg border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    />
                  </div>
                </div>
              )}
            </div>

            <Button
              type="submit"
              className="w-full glow-primary"
              size="lg"
              disabled={loading || (mode === 'improve' && !sourceRunId)}
            >
              {loading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : mode === 'improve' ? (
                <RefreshCw className="w-4 h-4 mr-2" />
              ) : (
                <Rocket className="w-4 h-4 mr-2" />
              )}
              {mode === 'improve' ? 'Lancer l\'amélioration' : 'Lancer la génération'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
